import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Alert,
  Stack,
  MenuItem,
  InputAdornment,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  CardContent,
  Card,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { Button } from '../../UI/Button';
import { disabledTextFieldStyles } from './utils';

interface SalesExperienceData {
  mediationCount?: number;
  monthlyMaxSales?: number;
  totalSales?: number;
  realEstateExperienceMonths?: number;
  entrepreneurialInterest?: boolean;
}

interface FieldErrors {
  mediationCount?: string;
  monthlyMaxSales?: string;
  totalSales?: string;
  realEstateExperienceMonths?: string;
  entrepreneurialInterest?: string;
}

interface SalesExperienceFormProps {
  formData: SalesExperienceData;
  isEditing: boolean;
  isUpdating: boolean;
  error: string | null;
  onInputChange: (field: keyof SalesExperienceData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onClearError: () => void;
}

export const SalesExperienceForm: React.FC<SalesExperienceFormProps> = ({
  formData,
  isEditing,
  isUpdating,
  error,
  onInputChange,
  onSave,
  onCancel,
  onEdit,
  onClearError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  
  // フォーカス状態管理
  const [focusedField, setFocusedField] = React.useState<string | null>(null);

  // バリデーション関数
  const validateField = (field: keyof SalesExperienceData, value?: number | boolean): string | undefined => {
    // 数値フィールドのバリデーション
    if (['mediationCount', 'monthlyMaxSales', 'totalSales', 'realEstateExperienceMonths'].includes(field)) {
      const numericValue = value as number;
      if (numericValue !== undefined && numericValue !== null) {
        switch (field) {
          case 'mediationCount':
            if (numericValue < 0) return '仲介実績は0以上で入力してください';
            if (numericValue > 99999) return '仲介実績は99999以下で入力してください';
            break;
          case 'monthlyMaxSales':
            if (numericValue < 0) return '月間最高売り上げは0以上で入力してください';
            if (numericValue > 2147483647) return '月間最高売り上げは約21億円以下で入力してください';
            break;
          case 'totalSales':
            if (numericValue < 0) return '総売り上げは0以上で入力してください';
            if (numericValue > 2147483647) return '総売り上げは約21億円以下で入力してください';
            break;
          case 'realEstateExperienceMonths':
            if (numericValue < 0) return '不動産歴は0以上で入力してください';
            if (numericValue > 1200) return '不動産歴は1200ヶ月（100年）以下で入力してください';
            break;
        }
      }
    }
    return undefined;
  };

  // 全フィールドのバリデーション
  const validateAllFields = (): FieldErrors => {
    const errors: FieldErrors = {};
    
    const mediationError = validateField('mediationCount', formData.mediationCount);
    if (mediationError) errors.mediationCount = mediationError;
    
    const monthlyMaxSalesError = validateField('monthlyMaxSales', formData.monthlyMaxSales);
    if (monthlyMaxSalesError) errors.monthlyMaxSales = monthlyMaxSalesError;
    
    const totalSalesError = validateField('totalSales', formData.totalSales);
    if (totalSalesError) errors.totalSales = totalSalesError;
    
    const realEstateError = validateField('realEstateExperienceMonths', formData.realEstateExperienceMonths);
    if (realEstateError) errors.realEstateExperienceMonths = realEstateError;
    
    return errors;
  };

  // 金額をフォーマット（3桁区切り）- 簡素化版
  const formatCurrency = (value?: number): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '';
    }
    return value.toLocaleString();
  };

  // 表示用の値を取得（フォーカス状態に応じて切り替え）
  const getDisplayValue = (field: keyof SalesExperienceData): string => {
    const value = formData[field] as number;
    
    // フォーカス中は生の数値文字列、そうでなければフォーマット済み
    if (focusedField === field) {
      return value === undefined || value === null ? '' : value.toString();
    }
    
    return formatCurrency(value);
  };

  // 入力値の変換（カンマを除去して数値として処理）- 改善版
  const handleCurrencyChange = (field: keyof SalesExperienceData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // 入力値からカンマを除去
    const rawValue = event.target.value.replace(/,/g, '');
    
    // 疑似的なイベントオブジェクトを作成（親に生の数値文字列を送信）
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: rawValue,
      },
    };
    
    // 親のhandleInputChangeを呼び出し
    onInputChange(field)(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  // フォーカス時の処理
  const handleCurrencyFocus = (field: keyof SalesExperienceData) => (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    setFocusedField(field);
  };

  // フォーカスアウト時の処理（バリデーションのみ）
  const handleCurrencyBlur = (field: keyof SalesExperienceData) => (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    setFocusedField(null);
    
    // フォーカスアウト時にバリデーションを実行
    const currentValue = formData[field] as number;
    const fieldError = validateField(field, currentValue);
    setFieldErrors(prev => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  // 数値入力の処理 - 改善版
  const handleNumberChange = (field: keyof SalesExperienceData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // 数値入力時も基本的な処理のみ（リアルタイムバリデーション削除）
    onInputChange(field)(event);
  };

  // 数値フィールドのフォーカス処理
  const handleNumberFocus = (field: keyof SalesExperienceData) => (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    setFocusedField(field);
  };

  // 数値フィールドのフォーカスアウト処理
  const handleNumberBlur = (field: keyof SalesExperienceData) => (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    setFocusedField(null);
    
    // フォーカスアウト時にバリデーションを実行
    const currentValue = formData[field] as number;
    const fieldError = validateField(field, currentValue);
    setFieldErrors(prev => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  // 保存処理（バリデーション付き）
  const handleSave = () => {
    const errors = validateAllFields();
    setFieldErrors(errors);
    
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      // エラーがある場合は保存を阻止
      return;
    }
    
    // バリデーション成功時のみ保存実行
    onSave();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          mb={3}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box display="flex" alignItems="center">
            <WorkIcon color="primary" fontSize="large" sx={{ mr: 1 }} />
            <Typography 
              variant="h5" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
            >
              営業実績・経験情報
            </Typography>
          </Box>
          {!isEditing && (
            <Button
              variant="outline"
              size={isMobile ? "small" : "medium"}
              startIcon={<EditIcon />}
              onClick={onEdit}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              編集
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* 経験・実績セクション */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <TrendingUpIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                経験・実績
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="仲介実績"
              value={getDisplayValue('mediationCount')}
              onChange={handleNumberChange('mediationCount')}
              onFocus={handleNumberFocus('mediationCount')}
              onBlur={handleNumberBlur('mediationCount')}
              disabled={!isEditing || isUpdating}
              type="text"
              inputMode="numeric"
              placeholder="0"
              error={!!fieldErrors.mediationCount}
              InputProps={{
                startAdornment: <TrendingUpIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: <InputAdornment position="end">件</InputAdornment>,
              }}
              sx={{
                ...disabledTextFieldStyles,
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
              helperText={fieldErrors.mediationCount || "これまでの不動産仲介実績件数"}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="不動産歴"
              value={getDisplayValue('realEstateExperienceMonths')}
              onChange={handleNumberChange('realEstateExperienceMonths')}
              onFocus={handleNumberFocus('realEstateExperienceMonths')}
              onBlur={handleNumberBlur('realEstateExperienceMonths')}
              disabled={!isEditing || isUpdating}
              type="text"
              inputMode="numeric"
              placeholder="0"
              error={!!fieldErrors.realEstateExperienceMonths}
              InputProps={{
                startAdornment: <ScheduleIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: <InputAdornment position="end">ヶ月</InputAdornment>,
              }}
              sx={{
                ...disabledTextFieldStyles,
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
              helperText={fieldErrors.realEstateExperienceMonths || "不動産業界での経験期間"}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>

          {/* 売上実績セクション */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2} mt={2}>
              <MoneyIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                売上実績
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="月間最高売り上げ"
              value={getDisplayValue('monthlyMaxSales')}
              onChange={handleCurrencyChange('monthlyMaxSales')}
              onFocus={handleCurrencyFocus('monthlyMaxSales')}
              onBlur={handleCurrencyBlur('monthlyMaxSales')}
              disabled={!isEditing || isUpdating}
              type="text"
              placeholder="0"
              error={!!fieldErrors.monthlyMaxSales}
              InputProps={{
                startAdornment: <MoneyIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: <InputAdornment position="end">円</InputAdornment>,
              }}
              sx={{
                ...disabledTextFieldStyles,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: !isEditing && formData.monthlyMaxSales ? 'warning.lighter' : 'inherit',
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
              helperText={fieldErrors.monthlyMaxSales || "これまでの月間売上最高記録"}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="総売り上げ"
              value={getDisplayValue('totalSales')}
              onChange={handleCurrencyChange('totalSales')}
              onFocus={handleCurrencyFocus('totalSales')}
              onBlur={handleCurrencyBlur('totalSales')}
              disabled={!isEditing || isUpdating}
              type="text"
              placeholder="0"
              error={!!fieldErrors.totalSales}
              InputProps={{
                startAdornment: <MoneyIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: <InputAdornment position="end">円</InputAdornment>,
              }}
              sx={{
                ...disabledTextFieldStyles,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: !isEditing && formData.totalSales ? 'success.lighter' : 'inherit',
                },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
              helperText={fieldErrors.totalSales || "これまでの累計売上実績"}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>

          {/* 将来の志向セクション */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2} mt={2}>
              <FlagIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                将来の志向
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset" disabled={!isEditing || isUpdating}>
              <FormLabel 
                component="legend"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                起業志向
              </FormLabel>
              <RadioGroup
                row={!isMobile}
                value={formData.entrepreneurialInterest === true ? 'true' : formData.entrepreneurialInterest === false ? 'false' : ''}
                onChange={(event) => {
                  // 元の文字列値をそのまま渡す
                  onInputChange('entrepreneurialInterest')(event);
                }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio size={isMobile ? 'small' : 'medium'} />}
                  label="あり"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.9rem', sm: '1rem' } } }}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size={isMobile ? 'small' : 'medium'} />}
                  label="なし"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.9rem', sm: '1rem' } } }}
                />
              </RadioGroup>
              <FormHelperText sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                将来的に独立・起業を考えているか
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        {isEditing && (
          <Box 
            display="flex" 
            gap={2} 
            mt={3}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
              size={isMobile ? 'small' : 'medium'}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {isUpdating ? '保存中...' : '保存'}
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isUpdating}
              size={isMobile ? 'small' : 'medium'}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              キャンセル
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}; 